import React, { createContext, cloneElement, createElement, PureComponent, useContext, useCallback } from 'react';
import memoizeOne from 'memoize-one';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { t } from '@bookingcom/lingojs-core';
export { I18n, t } from '@bookingcom/lingojs-core';
import { isElement, isValidElementType } from 'react-is';
export { SupportedLocales, localeToBookingLangCode, locales } from '@bookingcom/lingojs-locales';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var I18nContext = createContext(null);
var I18nContextProvider = I18nContext.Provider, I18nContextConsumer = I18nContext.Consumer;
var I18nProvider = /** @class */ (function (_super) {
    __extends(I18nProvider, _super);
    function I18nProvider(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            showTags: props.store.showTags(),
            language: props.store.getLanguage(),
        };
        _this.getContextValue = memoizeOne(function (store, language, showTags) {
            var setLanguage = function (language, messages, pluralFunc) {
                store.setLanguage(language, messages, pluralFunc);
                _this.setState({ language: language });
            };
            var setShowTags = function (showTags) {
                store.setShowTags(showTags);
                _this.setState({ showTags: showTags });
            };
            return {
                trans: store.trans.bind(store),
                setLanguage: setLanguage,
                setShowTags: setShowTags,
                showTags: store.showTags(),
                language: store.getLanguage(),
            };
        });
        return _this;
    }
    I18nProvider.prototype.render = function () {
        var context = this.getContextValue(this.props.store, this.state.language, this.state.showTags);
        return (React.createElement(I18nContextProvider, { value: context }, this.props.children));
    };
    return I18nProvider;
}(React.Component));

function withI18n(Component) {
    var I18nComponent = function (props) { return (React.createElement(I18nContextConsumer, null, function (ctx) { return React.createElement(Component, __assign({}, props, { i18n: ctx })); })); };
    return hoistNonReactStatics(I18nComponent, Component);
}

// match <0>paired</0> and <1/> unpaired tags
var tagRe = /<(\d+|ol|ul|li|u|p|s|strong|data|em|i|b)\s*>(.*?)<\/\1\s*>|<(\d+|br)\s*\/>/;
var nlRe = /(?:\r\n|\r|\n)/g;
// This list is not expected to expand.
// Please consider why you can't solve your problem by passing custom components before adding more elements here
var defaultRichTextElements = new Set([
    'b',
    'br',
    'data',
    'em',
    'i',
    'li',
    'ol',
    'p',
    's',
    'strong',
    'u',
    'ul',
]);
/**
 * Replaces html tags (i.e.: <0>This is a link</0>) for valid React elements.
 * It is used when we need to translate links, or add some special markup on
 * translations. This is used in all tags which has variables like start_link
 * and end_link
 * @param text The translated string returned from i18n.trans(...)
 * @param components The components to replace for <0> elements
 */
function formatString(text, components, originalTag) {
    var e_1, _a;
    var parts = text.replace(nlRe, '').split(tagRe);
    // no inline elements, return
    if (parts.length === 1) {
        return text;
    }
    var tree = [];
    var before = parts.shift();
    if (before)
        tree.push(before);
    var key = 0;
    try {
        for (var _b = __values(getElements(parts)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 3), elem_type = _d[0], children = _d[1], after = _d[2];
            var element = (components && components[parseInt(elem_type, 10)]) ||
                (defaultRichTextElements.has(elem_type) && elem_type);
            if (!element) {
                // TODO: Add proper error message
                console.error("MISSING COMPONENT for </".concat(elem_type, "> in translation of ").concat(originalTag, ". Please pass a components array to your <Trans> component calls"));
                tree.push('💢');
            }
            else {
                key += 1;
                if (isElement(element)) {
                    tree.push(cloneElement(element, { key: key }, 
                    // format children for pair tags
                    // unpaired tags might have children if it's a component passed as a variable
                    children
                        ? formatString(children, components, originalTag)
                        : element.props.children));
                }
                else if (isValidElementType(element)) {
                    tree.push(createElement(element, { key: key }, children
                        ? formatString(children, components, originalTag)
                        : undefined));
                }
                else {
                    console.error("UNKNOWN COMPONENT PASSED for </".concat(elem_type, "> while translating of ").concat(originalTag));
                    tree.push('💢');
                    continue;
                }
            }
            if (after) {
                tree.push(after);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return tree;
}
/*
 * `getElements` - return array of element indexes and element childrens
 *
 * `parts` is array of [pairedIndex, children, unpairedIndex, textAfter, ...]
 * where:
 * - `pairedIndex` is index of paired element (undef for unpaired)
 * - `children` are children of paired element (undef for unpaired)
 * - `unpairedIndex` is index of unpaired element (undef for paired)
 * - `textAfter` is string after all elements (empty string, if there's nothing)
 *
 * `parts` length is always multiply of 4
 *
 * Returns: Array<[elementIndex, children, after]>
 */
function getElements(parts) {
    if (!parts.length)
        return [];
    var _a = __read(parts.slice(0, 4), 4), paired = _a[0], children = _a[1], unpaired = _a[2], after = _a[3];
    return [[paired || unpaired, children || '', after]].concat(getElements(parts.slice(4, parts.length)));
}

var Trans = /** @class */ (function (_super) {
    __extends(Trans, _super);
    function Trans() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Trans.prototype.render = function () {
        var _a = this.props, _b = _a.form, form = _b === void 0 ? 'name' : _b, item = _a.item, list = _a.list, num_exception = _a.num_exception, tag = _a.tag, variables = _a.variables, components = _a.components, i18n = _a.i18n;
        var fallback = '💢';
        if (!i18n) {
            if (process.env.NODE_ENV !== 'production') {
                console.error("Failed to translate message using <Trans /> because <I18nProvider /> is missing in the app's context. Falling back to an empty string.");
            }
            return fallback;
        }
        if (typeof tag === 'string' && tag.length > 0) {
            // tags
            return formatString(i18n.trans(t(tag, { num_exception: num_exception, variables: variables })), components, tag);
        }
        // lists
        if (typeof list === 'string' && list.length > 0) {
            if (!item) {
                console.error("Failed to translate list message `".concat(list, "` using <Trans />. Please specify the 'item' prop."));
                return fallback;
            }
            return formatString(i18n.trans(t(list, item, form, { num_exception: num_exception, variables: variables })), components, "".concat(list, "/").concat(item, "/").concat(form));
        }
        console.error("Failed to translate message using <Trans />, because some are props missing. Please specify the 'tag' prop or 'list' and 'item' props.");
        return fallback;
    };
    return Trans;
}(PureComponent));
var Trans$1 = withI18n(Trans);

// tslint:disable:variable-name
function useI18n() {
    var i18n = useContext(I18nContext);
    if (!i18n) {
        throw new Error("Couldn't find a I18n Provider.");
    }
    return i18n;
}

function useTranslations() {
    var i18n = useI18n();
    var translate = useCallback(function (tag) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return i18n.trans(t.apply(void 0, __spreadArray([tag], __read(args), false)));
    }, [i18n.language, i18n.showTags]);
    return { translate: translate, i18n: i18n };
}

export { I18nProvider, Trans$1 as Trans, useI18n, useTranslations, withI18n };
//# sourceMappingURL=index.es.js.map
